import React from "react";
import { motion } from "framer-motion";
import { MapPin, Phone, Mail, Globe, Facebook, Instagram, Linkedin } from "lucide-react";
import { useLanguage } from "../contexts/LanguageContext";

export default function Footer({ onNavigate, onOpenPrivacyPolicy, onOpenCookiePolicy, onOpenTermsOfService }) {
  const { t } = useLanguage();
  const containerAnimation = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
        delayChildren: 0.2,
      },
    },
  };

  const itemAnimation = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        type: "spring",
        stiffness: 100,
        damping: 15,
      },
    },
  };

  return (
    <footer className="bg-gradient-to-b from-gray-900 via-gray-900 to-black text-white relative overflow-hidden">
      {/* Background decoration */}
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute top-10 right-20 w-64 h-64 bg-[--accent] rounded-full opacity-5 blur-3xl" />
        <div className="absolute bottom-10 left-20 w-80 h-80 bg-gray-600 rounded-full opacity-3 blur-3xl" />
      </div>

      <motion.div
        variants={containerAnimation}
        initial="hidden"
        animate="visible"
        className="max-w-7xl mx-auto px-6 py-16 relative z-10"
      >
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8 mb-12">
          {/* Company Info */}
          <motion.div variants={itemAnimation} className="lg:col-span-2">
            <div className="mb-6">
              <h3 className="text-2xl font-display font-bold text-[--accent] mb-4">
                ELLEGI
              </h3>
              <p className="text-gray-300 leading-relaxed mb-4">
                {t.footer.companyDesc}
              </p>
              <p className="text-sm text-gray-400">
                {t.footer.companyTagline}
              </p>
            </div>

            {/* Social Links */}
            <div className="flex space-x-4">
              <motion.a
                href="https://www.instagram.com/ellegi_illuminazione/"
                target="_blank"
                rel="noopener noreferrer"
                className="w-10 h-10 bg-[--accent]/20 rounded-xl flex items-center justify-center hover:bg-[--accent] transition-colors group"
                whileHover={{ scale: 1.1 }}
                whileTap={{ scale: 0.95 }}
              >
                <Instagram className="w-5 h-5 text-[--accent] group-hover:text-white transition-colors" />
              </motion.a>
              <motion.a
                href="https://www.lg-mi.it"
                target="_blank"
                rel="noopener noreferrer"
                className="w-10 h-10 bg-[--accent]/20 rounded-xl flex items-center justify-center hover:bg-[--accent] transition-colors group"
                whileHover={{ scale: 1.1 }}
                whileTap={{ scale: 0.95 }}
              >
                <Globe className="w-5 h-5 text-[--accent] group-hover:text-white transition-colors" />
              </motion.a>
            </div>
          </motion.div>

          {/* Contact Info */}
          <motion.div variants={itemAnimation}>
            <h4 className="text-lg font-display font-semibold text-white mb-6">
              {t.footer.contactsTitle}
            </h4>
            <div className="space-y-4">
              <div className="flex items-start space-x-3">
                <MapPin className="w-5 h-5 text-[--accent] mt-1 flex-shrink-0" />
                <div>
                  <p className="text-gray-300 text-sm">
                    Viale Vittorio Veneto, 6<br />
                    20124 MILANO, Italy
                  </p>
                </div>
              </div>
              <div className="flex items-center space-x-3">
                <Phone className="w-5 h-5 text-[--accent] flex-shrink-0" />
                <p className="text-gray-300 text-sm">+39 02 2895741</p>
              </div>
              <div className="flex items-center space-x-3">
                <Mail className="w-5 h-5 text-[--accent] flex-shrink-0" />
                <p className="text-gray-300 text-sm">comm@lg-mi.it</p>
              </div>
            </div>
          </motion.div>

          {/* Quick Links */}
          <motion.div variants={itemAnimation}>
            <h4 className="text-lg font-display font-semibold text-white mb-6">
              {t.footer.quickLinksTitle}
            </h4>
            <div className="space-y-3">
              <button
                onClick={() => onNavigate?.('about')}
                className="block text-left text-gray-300 hover:text-[--accent] transition-colors text-sm w-full"
              >
                {t.footer.quickAbout}
              </button>
              <button
                onClick={() => onNavigate?.('projects')}
                className="block text-left text-gray-300 hover:text-[--accent] transition-colors text-sm w-full"
              >
                {t.footer.quickProjects}
              </button>
              <button
                onClick={() => onNavigate?.('news')}
                className="block text-left text-gray-300 hover:text-[--accent] transition-colors text-sm w-full"
              >
                {t.footer.quickNews}
              </button>
              <button
                onClick={() => onNavigate?.('collaborations')}
                className="block text-left text-gray-300 hover:text-[--accent] transition-colors text-sm w-full"
              >
                {t.footer.quickCollaborations}
              </button>
              <button
                onClick={() => onNavigate?.('files')}
                className="block text-left text-gray-300 hover:text-[--accent] transition-colors text-sm w-full"
              >
                {t.footer.quickInfo}
              </button>
              <button
                onClick={() => onNavigate?.('contacts')}
                className="block text-left text-gray-300 hover:text-[--accent] transition-colors text-sm w-full"
              >
                {t.footer.quickContacts}
              </button>
            </div>
          </motion.div>
        </div>

        {/* Bottom Bar */}
        <motion.div
          variants={itemAnimation}
          className="border-t border-gray-700 pt-8 flex flex-col md:flex-row justify-between items-center space-y-4 md:space-y-0"
        >
          <div className="text-center md:text-left">
            <p className="text-gray-400 text-sm">
              {t.footer.allRights}
            </p>
            <p className="text-gray-500 text-xs mt-1">
              {t.footer.vatInfo}
            </p>
          </div>

          <div className="flex space-x-6 text-xs text-gray-400">
            <button
              onClick={onOpenPrivacyPolicy}
              className="hover:text-[--accent] transition-colors cursor-pointer"
            >
              Privacy Policy
            </button>
            <button
              onClick={onOpenTermsOfService}
              className="hover:text-[--accent] transition-colors cursor-pointer"
            >
              {t.footer.termsOfService}
            </button>
            <button
              onClick={onOpenCookiePolicy}
              className="hover:text-[--accent] transition-colors cursor-pointer"
            >
              Cookie Policy
            </button>
          </div>
        </motion.div>
      </motion.div>

      {/* Top gradient overlay for seamless transition */}
      <div className="absolute -top-32 left-0 right-0 h-32 bg-gradient-to-b from-gray-900 to-transparent pointer-events-none" />
      <div className="absolute top-0 left-0 right-0 h-16 bg-gradient-to-b from-gray-900 to-transparent pointer-events-none" />
    </footer>
  );
}
